import type { Proto } from "../../pojo/Proto";
import { S25Number } from "./s25.number";

export namespace S25Color {
    //http://stackoverflow.com/questions/5623838/rgb-to-hex-and-hex-to-rgb
    export function componentToHex(c: number): string {
        const hex: string = c.toString(16);
        return hex.length === 1 ? "0" + hex : hex;
    }
    /**
     * CSS `#` hex → RGBA. Optional `#`; lengths 3 / 4 / 6 / 8 (`#rgb`, `#rgba`, `#rrggbb`, `#rrggbbaa`).
     * Returns null if empty or not one of those forms.
     */
    export function hexToRGBA(hex: string): { r: number; g: number; b: number; a: number; alphaInHex: boolean } | null {
        let h = (hex || "").trim();
        if (h.startsWith("#")) {
            h = h.slice(1);
        }
        if (!h || !/^[0-9a-f]+$/i.test(h)) {
            return null;
        }
        const len = h.length;
        const expandPair = (c: string) => parseInt(c + c, 16);
        if (len === 3) {
            return {
                r: expandPair(h[0]),
                g: expandPair(h[1]),
                b: expandPair(h[2]),
                a: 255,
                alphaInHex: false,
            };
        }
        if (len === 4) {
            return {
                r: expandPair(h[0]),
                g: expandPair(h[1]),
                b: expandPair(h[2]),
                a: expandPair(h[3]),
                alphaInHex: true,
            };
        }
        if (len === 6) {
            const n = parseInt(h, 16);
            return {
                r: (n >> 16) & 255,
                g: (n >> 8) & 255,
                b: n & 255,
                a: 255,
                alphaInHex: false,
            };
        }
        if (len === 8) {
            const n = parseInt(h, 16);
            return {
                r: (n >>> 24) & 255,
                g: (n >>> 16) & 255,
                b: (n >>> 8) & 255,
                a: n & 255,
                alphaInHex: true,
            };
        }
        return null;
    }

    export function hexToRGB(hex: string): { r: number; g: number; b: number } {
        const v = hexToRGBA(hex);
        return v ? { r: v.r, g: v.g, b: v.b } : { r: 0, g: 0, b: 0 };
    }

    /** Six hex digits, no `#`. */
    export function rgbToHex(r: number, g: number, b: number): string {
        return componentToHex(r) + componentToHex(g) + componentToHex(b);
    }

    /**
     * Lowercase `#rrggbb` for `<input type="color">` (alpha dropped). Uses {@link hexToRGBA} / {@link rgbToHex}.
     * Note: not all browsers support `#rrggbbaa` (alpha) for `<input type="color">`.
     */
    export function hexForColorPicker(value: string, fallback: string): string {
        let rgba = hexToRGBA(value);
        if (!rgba) {
            rgba = hexToRGBA(fallback);
        }
        if (!rgba) {
            return "#000000";
        }
        return `#${rgbToHex(rgba.r, rgba.g, rgba.b)}`.toLowerCase();
    }

    export function rgbStringToRGB(colorString: string): any {
        const arr: string[] = colorString.replace("rgb(", "").replace(")", "").replace(" ", "").split(",");
        return {
            r: S25Number.parseInt(arr[0]),
            g: S25Number.parseInt(arr[1]),
            b: S25Number.parseInt(arr[2]),
        };
    }

    export function rgbStringToHex(colorString: string): string {
        const rgb = rgbStringToRGB(colorString);
        return rgbToHex(rgb.r, rgb.g, rgb.b);
    }

    export function colorBrightness(hex: string): number {
        //http://www.blitzbasic.com/Community/posts.php?topic=87307
        const rgb = hexToRGB(hex);
        return Math.sqrt(
            S25Number.parseFloat(rgb.r * rgb.r) * 0.241 +
                S25Number.parseFloat(rgb.g * rgb.g) * 0.691 +
                S25Number.parseFloat(rgb.b * rgb.b * 0.068),
        ); //measure of brightness from 0-255, higher is brighter
    }
    export function interpolateColor(index: Proto.Fraction, start: Proto.HexColor, end: Proto.HexColor) {
        if (!index) return start;
        const startRGB = hexToRGB(start.replace(/^#/, ""));
        const endRGB = hexToRGB(end.replace(/^#/, ""));
        const resultRGB = { r: 0, g: 0, b: 0 };
        for (const c of ["r", "g", "b"] as const)
            resultRGB[c] = Math.round(startRGB[c] + index * (endRGB[c] - startRGB[c]));
        return "#" + rgbToHex(resultRGB.r, resultRGB.g, resultRGB.b);
    }
}
